import i18n from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppearanceStore } from '@/store/appearanceStore'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { useOpenDocumentStore } from "@/store/openDocumentStore";
import { useLocation, useNavigate } from 'react-router'
import { ThemePicker } from '@/components/theme/ThemePicker'
import { IconPalette,
    IconSearch,
    IconMoon,
    IconSun,
    IconSettings,
    IconLogout,
    IconHelpCircle,
} from '@tabler/icons-react'
import { Launcher } from './Launcher'
import { liliaMenu, useLiliaMenu } from './liliaMenuStore'
import { NewDocumentDialog } from '@/components/dialogs/NewDocumentDialog'
import { AppCrumb } from '@/components/navigation/AppCrumb'
import { testIds } from '@/testIds'
import { Badge } from '@/components/lilia-ui/badge'
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from '@/components/lilia-ui/dropdown-menu'
import { useAuth } from '@/auth'
import { useThemeStore } from '@/store/themeStore'
import { DARK_FAMILY } from '@/utils/themeVariant'

/**
 * Lilia top bar — the single 48px chrome row that replaces the persistent
 * left sidebar on authed shell pages (Claude Design Handoff 19, §1a).
 *
 * Left→right: the Lilia button (L) · the crumb (`L › Tool › item`; in the
 * editor, the view the paper came from) · spacer · ⌘K trigger ·
 * + New · theme toggle · avatar. Tokens reference the existing --lilia-*
 * layer so it follows the active theme. The editor (/d/:id) keeps
 * its own dedicated TopBar — this bar is for dashboard/docs/teams/etc.
 */

interface LiliaTopBarProps {
    onOpenPalette: () => void
    /** Document-scoped controls (save state · Export · Share · class · ⋮).
     *  Compact chrome puts them in this bar rather than a second row — see
     *  lilia-docs/design-handoffs/2026-09-11-editor-chrome/ §4. Empty off a
     *  document, which is why the bar itself knows nothing about documents. */
    documentSlot?: React.ReactNode
    /** Sits right after the document's name: the save indicator, whose answer
     *  is about the document, so it belongs beside the document's name
     *  (saved-moment handoff, 27 Sep). */
    titleAccessory?: React.ReactNode
}

export function LiliaTopBar({ onOpenPalette, documentSlot, titleAccessory }: LiliaTopBarProps) {
    // Empty unless a document is open, which is exactly when the bar has
    // something worth centring. Subscribing to the one field keeps the shell
    // from re-rendering on every block edit.
    // Deliberately not editorStore: see openDocumentStore for what that
    // import cost. EditorPage keeps these in step.
    const openDocumentTitle = useOpenDocumentStore((s) => s.title);
    const renameOpenDocument = useOpenDocumentStore((s) => s.onRename);
    // A viewer's document is open too, just not renamable: it carries sharedBy.
    const sharedBy = useOpenDocumentStore((s) => s.sharedBy);
    const hasOpenDocument = renameOpenDocument !== null || sharedBy !== null;
    // "Rename" in the editor's ⋯ edits this title in place, fully selected —
    // the same gesture as a card's Rename (documents-actions handoff §2).
    const renameRequest = useOpenDocumentStore((s) => s.renameRequest);
    const titleInput = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (renameRequest === 0) return;
        titleInput.current?.focus();
        titleInput.current?.select();
    }, [renameRequest]);

    const navigate = useNavigate()
    const { user, signOut } = useAuth()
    const themeName = useThemeStore((s) => s.themeName)
    const setThemeName = useThemeStore((s) => s.setThemeName)
    const setAppearance = useAppearanceStore((s) => s.setAppearance)
    const isDarkFamily = DARK_FAMILY.includes(themeName ?? 'light')
    const [themePickerOpen, setThemePickerOpen] = useState(false)
    // The Lilia menu: L opens it browsing, ⌘K and the search field typing.
    const menu = useLiliaMenu()
    const launcherOpen = menu.open
    const { pathname } = useLocation()
    useEffect(() => { liliaMenu.pathChanged(pathname) }, [pathname])
    // The New-document chooser, for every surface that asks for it — the
    // menu's New document, My documents' New, Home's New paper.
    const [newDocOpen, setNewDocOpen] = useState(false)
    const [newDocView, setNewDocView] = useState<'chooser' | 'template'>('chooser')
    useEffect(() => {
        const onNew = (e: Event) => {
            setNewDocView((e as CustomEvent<{ view?: 'template' } | undefined>).detail?.view === 'template' ? 'template' : 'chooser')
            setNewDocOpen(true)
        }
        window.addEventListener('lilia:new-document', onNew)
        return () => window.removeEventListener('lilia:new-document', onNew)
    }, [])


    const initials = useMemo(() => {
        const name = (user?.userName || user?.email || '').trim()
        if (!name) return '··'
        const parts = name.split(/[\s@.]+/).filter(Boolean)
        const first = parts[0]?.[0] ?? ''
        const second = parts[1]?.[0] ?? ''
        return (first + second).toUpperCase() || first.toUpperCase()
    }, [user])

    return (
        <header
            data-testid={testIds.appBar.root}
            className="flex items-center shrink-0 relative z-50 select-none"
            style={{
                height: 'var(--bar-h, 48px)',
                paddingInline: 10,
                gap: 4,
                background: 'var(--lilia-surface)',
                borderBottom: '1px solid var(--lilia-border)',
            }}
        >
            {/* App switcher → launcher mega-menu (handoff §3) */}
            <button
                type="button"
                onClick={() => liliaMenu.toggle('browse')}
                data-testid="app-switcher"
                data-open={launcherOpen}
                className="flex items-center rounded-[10px] border transition-colors"
                style={{
                    gap: 3,
                    height: 30,
                    padding: '2px 4px 2px 2px',
                    borderColor: launcherOpen ? 'var(--lilia-border)' : 'transparent',
                    background: launcherOpen ? 'var(--lilia-hover)' : 'transparent',
                }}
                onMouseEnter={(e) => { if (!launcherOpen) e.currentTarget.style.background = 'var(--lilia-hover)' }}
                onMouseLeave={(e) => { if (!launcherOpen) e.currentTarget.style.background = 'transparent' }}
                aria-haspopup="menu"
                aria-expanded={launcherOpen}
                aria-label={i18n.t('shell:bar.switchWorkspace')}
            >
                <span
                    className="flex items-center justify-center rounded-lg text-white"
                    style={{ width: 26, height: 26, background: 'var(--lilia-primary)' }}
                >
                    {/* L + quill mark, same source polygons as Logo.tsx —
                        logos-for-editor-and-youtube/lilia_editor_logo.svg.
                        Tight viewBox crops straight to the shape's bounds so
                        it drops in at this size with no extra transform. */}
                    <svg width="18" height="22" viewBox="140 110 245 300" xmlns="http://www.w3.org/2000/svg">
                        <polygon points="150,150 150,370 310,370 280,310 220,310 220,150" fill="#ffffff" />
                        <polygon points="265,120 295,120 375,390 345,400" fill="#ffffff" />
                    </svg>
                </span>
                {/* The stacked LILIA / Lilia wordmark used to live here. The
                    mark already identifies the app, and in compact chrome the
                    bar's width is better spent on the document. */}
            </button>
            <Launcher open={menu.open} mode={menu.mode} onClose={liliaMenu.hide} />
            <NewDocumentDialog open={newDocOpen} initialView={newDocView} onClose={() => setNewDocOpen(false)} />

            {/* Where you are: `L › Tool › item`, or in the editor the view
                the paper was opened from (navigation handoff §1, §5). */}
            <AppCrumb />

            {/* The document's name, centred in the bar.

                This space was empty on every editor page while the title sat
                in the breadcrumb below, squeezed between a back link and the
                save indicator. A document's name is the one piece of chrome
                that should be unmissable, and the widest, emptiest strip on
                the screen was carrying nothing.

                In the flex flow rather than absolutely centred on the bar.
                Absolute centring was fine while the right side was nearly
                empty, but compact chrome puts the document's own controls
                there, and a 520px centred input then sits on top of them and
                swallows their clicks. Centred in the space that is actually
                free cannot overlap, and it lets the title truncate first when
                the bar gets tight. */}
            <div className="flex-1" />

            {(hasOpenDocument || titleAccessory) && (
                /* Three columns, the outer two always equal: the name stays
                   centred on the bar whatever the indicator beside it says, so
                   "Saves as you type" becoming "Saved" never nudges the title. */
                <div
                    data-testid="topbar-title-group"
                    className="grid min-w-0 shrink items-center"
                    // The indicator's column never goes below its text: at 0 it let
                    // "Saved" spill over the View only chip (10 Oct). When the bar is
                    // tight the empty left column gives way first.
                    style={{ maxWidth: "min(64%, 860px)", gridTemplateColumns: "minmax(0, 1fr) auto minmax(max-content, 1fr)" }}
                >
                    <span aria-hidden />
                    {hasOpenDocument && (
                        /* Sized to its text (an invisible copy sets the width), so
                           the indicator after it sits beside the name rather than
                           at the far end of a 520px field. */
                        <span className="hidden min-w-0 md:inline-grid" style={{ maxWidth: 520 }}>
                            <span
                                aria-hidden
                                className="invisible col-start-1 row-start-1 overflow-hidden whitespace-pre px-2 py-1 text-[13px] font-medium"
                                style={{ minWidth: 96 }}
                            >
                                {openDocumentTitle || i18n.t('shell:bar.untitled')}
                            </span>
                            <input
                                ref={titleInput}
                                type="text"
                                // An input's own width is ~20 characters, which
                                // would set the column wider than a short name:
                                // size 1 leaves the invisible copy in charge.
                                size={1}
                                data-testid="input-document-title"
                                aria-label={i18n.t('shell:bar.documentName')}
                                className={
                                    "col-start-1 row-start-1 w-full min-w-0 truncate rounded-md border-0 bg-transparent px-2 py-1 " +
                                    "text-[13px] font-medium text-[var(--lilia-text)] outline-none " +
                                    "hover:bg-[var(--lilia-hover)] focus:bg-[var(--lilia-hover)] " +
                                    "focus:ring-2 focus:ring-[var(--lilia-primary)]/40"
                                }
                                value={openDocumentTitle}
                                title={openDocumentTitle}
                                placeholder={i18n.t('shell:bar.untitled')}
                                readOnly={!renameOpenDocument}
                                onChange={(e) => renameOpenDocument?.(e.target.value)}
                            />
                        </span>
                    )}
                    {!hasOpenDocument && <span aria-hidden />}
                    <span className="flex min-w-0 items-center justify-start pl-1">{titleAccessory}</span>
                </div>
            )}

            {/* Whose document this is, for a reader who can't edit it
                (view-only handoff, Olivia 26 Sep): a neutral chip, because
                viewing isn't a problem. The owner's email on hover. */}
            {sharedBy && (
                <Badge
                    variant="secondary"
                    data-testid="topbar-view-only"
                    title={sharedBy.email}
                    className="ml-1 hidden shrink-0 whitespace-nowrap md:inline-flex"
                >
                    View only · shared by {sharedBy.name}
                </Badge>
            )}

            <div className="min-w-0 flex-1" />

            {documentSlot}

            {/* ⌘K trigger */}
            <button
                type="button"
                onClick={onOpenPalette}
                data-testid={testIds.appBar.cmdk}
                className="flex items-center gap-2 rounded-md transition-colors justify-center w-[30px] px-0 md:w-auto md:min-w-[190px] md:px-2.5 md:justify-start"
                style={{
                    height: 30,
                    fontSize: 12,
                    cursor: 'text',
                    color: 'var(--lilia-text-muted)',
                    background: 'var(--lilia-bg)',
                    border: '1px solid var(--lilia-border)',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--lilia-primary)'
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--lilia-border)'
                }}
                aria-label={i18n.t('shell:bar.search')}
            >
                <IconSearch size={14} className="shrink-0" />
                <span className="hidden md:block flex-1 text-left">{i18n.t('shell:bar.searchAnything')}</span>
                <span
                    className="hidden md:inline font-mono shrink-0"
                    style={{
                        fontSize: 10,
                        padding: '1px 4px',
                        borderRadius: 3,
                        border: '1px solid var(--lilia-border)',
                        background: 'var(--lilia-hover)',
                    }}
                >
                    ⌘K
                </span>
            </button>

            {/* New lives in the workspace toolbar (handoff §2: no documents
                controls in the appbar). Create is still reachable via ⌘K. */}

            {/* Light ⇄ dark in one click (the user, 10 Oct: "dark and light theme
                switch for the editor"). The picker, with accents, is in the avatar menu. */}
            <button
                type="button"
                onClick={() => {
                    const next = isDarkFamily ? 'light' : 'dark'
                    setThemeName(next)
                    // An explicit choice, shared with the phone's Appearance row.
                    setAppearance(next)
                }}
                data-testid={testIds.appBar.themeToggle}
                data-theme-name={themeName}
                className="flex items-center justify-center rounded-md transition-colors"
                style={{ width: 30, height: 30, color: 'var(--lilia-text-secondary)' }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--lilia-hover)'
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                }}
                aria-label={isDarkFamily ? i18n.t('shell:bar.toLight') : i18n.t('shell:bar.toDark')}
                aria-pressed={isDarkFamily}
                title={i18n.t('shell:bar.themeTitle', {
                    name: i18n.t(`shell:bar.${(themeName ?? (isDarkFamily ? 'dark' : 'light')) === 'dark' ? 'dark' : 'light'}`),
                    action: isDarkFamily ? i18n.t('shell:bar.toLight') : i18n.t('shell:bar.toDark'),
                })}
            >
                {isDarkFamily ? <IconSun size={14} /> : <IconMoon size={14} />}
            </button>
            <ThemePicker open={themePickerOpen} onOpenChange={setThemePickerOpen} />

            {/* Help — discoverability entry (handoff §2). Opens the command
                palette for now; a dedicated help surface lands later. */}
            <button
                type="button"
                onClick={onOpenPalette}
                data-testid="help"
                className="flex items-center justify-center rounded-md transition-colors"
                style={{ width: 30, height: 30, color: 'var(--lilia-text-secondary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lilia-hover)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                aria-label={i18n.t('shell:bar.help')}
                title={i18n.t('shell:bar.help')}
            >
                <IconHelpCircle size={15} />
            </button>

            {/* Notifications: left of the avatar (Olivia, 10 Oct). */}
            <NotificationBell />

            {/* Avatar */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        data-testid={testIds.appBar.account}
                        className="flex items-center justify-center rounded-full text-white select-none"
                        style={{
                            width: 28,
                            height: 28,
                            background: 'var(--lilia-primary)',
                            fontSize: 10.5,
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                        }}
                        title={user?.email || i18n.t('shell:bar.account')}
                        aria-label={i18n.t('shell:bar.account')}
                    >
                        {initials}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[200px]" data-testid="top-bar-avatar-menu">
                    {user?.email && (
                        <DropdownMenuLabel className="truncate text-[11px] font-normal text-[var(--lilia-text-muted)]">
                            {user.email}
                        </DropdownMenuLabel>
                    )}
                    <DropdownMenuItem onClick={() => setThemePickerOpen(true)} data-testid="avatar-menu-theme">
                        <IconPalette size={15} className="mr-2" /> {i18n.t('shell:bar.themeAccent')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="avatar-menu-settings">
                        <IconSettings size={15} className="mr-2" /> {i18n.t('shell:bar.settings')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => void signOut()} data-testid="avatar-menu-logout">
                        <IconLogout size={15} className="mr-2" /> {i18n.t('shell:bar.logOut')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </header>
    )
}
